import {
  Box,
  Checkbox,
  FormControl,
  Grid,
  ListItemText,
  MenuItem,
  Typography,
  withStyles,
} from '@material-ui/core';
import { FORM_ERROR } from 'final-form';
import isEmpty from 'lodash/isEmpty';
import { Select } from 'mui-rff';
import React, { FC, useContext } from 'react';
import { Form as FinalForm } from 'react-final-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Button, EmailTextField } from '..';
import AppContext from '../../context/AppContext';
import { useShopConfig } from '../../hooks/shopConfig';
import { getFilterOptions } from '../../shopConfig/configHelper';
import { Cadence, EmailSubscribeSource } from '../../types/apollo/generated/types.generated';
import { FilterId } from '../../types/filters/filters';
import { UpdateGeneralSavedSearchEmailSubscriptionParams } from '../../types/models/savedSearch';
import { RequestStatus } from '../../types/requestStatus';
import * as log from '../../util/log';
import { RELEASES_AND_DISCOUNTS_GROUP_ID } from '../../util/sendgrid';
import * as validators from '../../util/validators';
import { ButtonVariant } from '../Button/Button';

import css from './SubscribeForm.module.css';

const useInputStyles = makeStyles(() => ({
  root: (props: SubscribeFormProps) => ({
    '& label': {
      color: props.inputFieldTextColor || 'white',
      fontWeight: 'bold',
    },
    '& label.Mui-focused': {
      color: props.inputFieldTextColor || 'white',
    },
    '& .MuiSelect-icon': {
      color: props.inputFieldTextColor || 'white',
      top: 'unset',
    },
    '& .MuiInputLabel-outlined': {
      transform: 'translate(14px, 15px) scale(1)',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
    // This is needed so that the autocomplete backfill fills the whole input
    '& input': {
      padding: '0px 14px',
      height: '100%',
    },
    '& .MuiOutlinedInput-root': {
      color: props.inputFieldTextColor || 'white',
      fontWeight: 'bold',
      width: '100%',
      height: '48px',
      '& fieldset': {
        borderWidth: '1px',
        borderColor: props.inputFieldBorderColor || 'white',
        color: props.inputFieldTextColor || 'white',
      },
      '&:hover fieldset': {
        borderColor: props.inputFieldBorderColor || 'white',
      },
      '&.Mui-focused fieldset': {
        borderColor: props.inputFieldBorderColor || 'white',
      },
    },
  }),
}));

export interface SubscribeFormProps {
  onSubscribe: (params: UpdateGeneralSavedSearchEmailSubscriptionParams) => void;
  areSizesEnabled?: boolean;
  updateNotificationsStatusForBuilderComponent?: RequestStatus;
  // INPUT FIELD (applies to both dropdown and email fields)
  inputFieldBorderColor?: string;
  inputFieldTextColor?: string;
  // BUTTON
  buttonBackgroundColor?: string;
  buttonBorderColor?: string;
  buttonTextColor?: string;
  // SUCCESS MESSAGE
  successMessageColor?: string;
  successMessageTextAlign?: 'left' | 'center' | 'right';
}

interface SubscribeFormValues {
  email: string;
  sizes?: string[];
}

const SubscribeForm: FC<SubscribeFormProps> = (props) => {
  const {
    onSubscribe,
    areSizesEnabled = true,
    updateNotificationsStatusForBuilderComponent,
  } = props;
  const inputClasses = useInputStyles(props);

  const intl = useIntl();
  const shopConfig = useShopConfig();
  const { treetId } = useContext(AppContext);

  const { updateSavedSearchProgress, savedSearchSource, updateNotificationsStatus } =
    useSelector<any>((state) => state.user) as any;

  const shouldShowSuccessMessage =
    updateNotificationsStatusForBuilderComponent === RequestStatus.Success;

  // If subscribeFooterActionBuilderSectionId exists and subscription was successful, show thank you message
  if (shouldShowSuccessMessage) {
    return (
      <Typography
        variant="h2"
        style={{
          color: props.successMessageColor || 'black',
          textAlign: (props.successMessageTextAlign || 'center') as any,
          fontWeight: 'bold',
        }}
      >
        Thank you, your email is subscribed!
      </Typography>
    );
  }

  let sizeOptions: { key: string; label: string }[] | null = null;
  if (areSizesEnabled) {
    sizeOptions = getFilterOptions(shopConfig, FilterId.Size) || null;
  }

  const emailRequiredMessage = intl.formatMessage({ id: 'SignupForm.emailRequired' });
  const emailRequired = validators.required(emailRequiredMessage);
  const emailInvalidMessage = intl.formatMessage({ id: 'SignupForm.emailInvalid' });
  const emailValid = validators.emailFormatValid(emailInvalidMessage);

  const onSubscribeSubmit = (values: SubscribeFormValues) => {
    const { email, sizes } = values;

    const errors: any = {};
    // Only want to run this on submit
    const emailErrors = validators.composeValidators(emailRequired, emailValid)(email);
    if (emailErrors) {
      errors.email = emailErrors;
    }

    if (!isEmpty(errors)) return errors;

    const subscribeSource = savedSearchSource || EmailSubscribeSource.Footer;

    const genericError = 'Oops, something went wrong. Please try again.';
    try {
      return onSubscribe({
        email,
        sizes,
        groupId: RELEASES_AND_DISCOUNTS_GROUP_ID,
        subscribeSource,
        cadence: Cadence.Weekly,
      });
    } catch {
      log.error(
        new Error('[Subscribe Form] Could not subscribe email to sizes sizes'),
        'subscribe-form-failed',
        {
          treetId,
          email,
          sizes,
          subscribeSource,
        }
      );
      return { [FORM_ERROR]: genericError };
    }
  };

  return (
    <FinalForm
      onSubmit={onSubscribeSubmit}
      render={(fieldRenderProps) => {
        const { handleSubmit, submitting, submitError, values } = fieldRenderProps;
        const isDisabled = submitting;

        const subscribeButtonStyle: React.CSSProperties | undefined =
          props.buttonBackgroundColor || props.buttonBorderColor || props.buttonTextColor
            ? {
                ...(props.buttonBackgroundColor && {
                  backgroundColor: props.buttonBackgroundColor,
                }),
                ...(props.buttonBorderColor && { border: `1px solid ${props.buttonBorderColor}` }),
                ...(props.buttonTextColor && { color: props.buttonTextColor }),
              }
            : undefined;

        return (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={12} md={5}>
                <EmailTextField
                  id="email-subscribe"
                  name="email"
                  label="Email"
                  variant="outlined"
                  required
                  classes={{ root: inputClasses.root }}
                />
              </Grid>
              {sizeOptions && (
                <Grid item xs={12} md={4}>
                  {/* Multiple select code from https://material-ui.com/components/selects/ */}
                  <FormControl fullWidth variant="outlined" classes={{ root: inputClasses.root }}>
                    <Select
                      variant="outlined"
                      name="sizes"
                      label="Sizes"
                      id="sizes-subscribe"
                      multiple
                      renderValue={(selected: any) => selected.join(', ')}
                      required
                      inputProps={{
                        'aria-label': 'Select sizes for email notifications',
                      }}
                    >
                      {sizeOptions.map(({ key, label }) => (
                        <MenuItem key={key} value={key} aria-label="label">
                          <Checkbox
                            checked={(values?.sizes || []).indexOf(key) > -1}
                            color="default"
                          />
                          <ListItemText primary={label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} md={3}>
                <Button
                  type="submit"
                  inProgress={updateSavedSearchProgress}
                  disabled={isDisabled}
                  className={css.subscribeButton}
                  spinnerClassName={css.subscribeSpinner}
                  variant={ButtonVariant.Secondary}
                  {...(subscribeButtonStyle && { style: subscribeButtonStyle })}
                >
                  Subscribe
                </Button>
              </Grid>
              {!!submitError && (
                <Box pt={1} display="flex" justifyContent="center">
                  <Typography variant="body1" style={{ color: 'white' }}>
                    {submitError}
                  </Typography>
                </Box>
              )}
            </Grid>
          </form>
        );
      }}
    />
  );
};

export default SubscribeForm;
